import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @property {HTMLButtonElement} [resetButton] - The clear/reset button.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback?.();
    this.#updateResetButtonVisibility();
    const input = this.refs.searchPageInput;
    if (input) input.addEventListener('input', this.#updateResetButtonVisibility);
  }

  disconnectedCallback() {
    const input = this.refs?.searchPageInput;
    if (input) input.removeEventListener('input', this.#updateResetButtonVisibility);
    super.disconnectedCallback?.();
  }

  #updateResetButtonVisibility = () => {
    const input = this.refs?.searchPageInput;
    const resetButton = this.refs?.resetButton;
    if (resetButton) resetButton.hidden = !input?.value?.trim();
  };

  /**
   * Clears the search input only; does not submit the form.
   */
  clearSearchOnly = () => {
    const input = this.refs.searchPageInput;
    if (!input) return;
    input.value = '';
    this.#updateResetButtonVisibility();
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
